import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { DateClickArg } from '@fullcalendar/interaction';

import { ScheduleParam } from 'containers/ProductRegist/hooks/types';
import { EventSourceInput } from '@fullcalendar/core';
import { OptionSheetType } from 'containers/ProductRegist/components/OptionSelectRegist/hooks/useCalenderHooks';
import CustomCalendarBase from './CustomCalendarBase';

const ScheduleCardBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;

  padding: 4px 8px;
  width: 85px;

  border-radius: 6px;
  background: #f7f7f8;

  color: #414b56;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.056px;

  &:hover {
    border: 1px solid #414b56 !important;
    background: #fff !important;
  }
`;

export const WEEK_DAYS = [
  {
    name: '일',
    color: '#FF4242',
  },
  {
    name: '월',
    color: '#70737c',
  },
  {
    name: '화',
    color: '#70737c',
  },
  {
    name: '수',
    color: '#70737c',
  },
  {
    name: '목',
    color: '#70737c',
  },
  {
    name: '금',
    color: '#70737c',
  },
  {
    name: '토',
    color: '#06F',
  },
];

interface Props<T> {
  isMobile: boolean;
  calendarList?: ScheduleParam[];
  checkedDate: Date[];
  setCheckedDate: (date: Date[]) => void;
  handleInitScheduleItem: () => void;
  handleIsOpenUpdateOptionSheet: (param: {
    isOpen: boolean;
    sheetType: OptionSheetType;
    scheduleItem?: T;
  }) => void;
}

const CustomCalendarV2 = <T extends any>(props: Props<T>) => {
  const {
    isMobile,
    calendarList,
    checkedDate,
    setCheckedDate,
    handleInitScheduleItem,
    handleIsOpenUpdateOptionSheet,
  } = props;

  const [selectedDates, setSelectedDates] = useState<Date[]>([]);

  const events: EventSourceInput = calendarList?.map(item => {
    return {
      title: `${dayjs(item.term.startedAt).format('HH시 mm분')}/${dayjs(
        item.term.startedAt + item.term.duration,
      ).format('HH시 mm분')}`,
      start: dayjs(item.term.startedAt).format('YYYY-MM-DD'),
      extendedProps: { id: item.id },
    };
  });

  const handleDateClick = (info: DateClickArg) => {
    const date = dayjs(info.date).toDate();

    const isSelected = selectedDates.some(selectedDate =>
      dayjs(selectedDate).isSame(dayjs(date), 'day'),
    );

    if (isSelected) {
      const filteredDates = selectedDates.filter(selectedDate => {
        return !dayjs(selectedDate).isSame(dayjs(date), 'day');
      });

      setSelectedDates(filteredDates);
      setCheckedDate(filteredDates);
    } else {
      handleInitScheduleItem();
      setSelectedDates([...selectedDates, date]);
      setCheckedDate([...checkedDate, date]);
    }
  };

  useEffect(() => {
    if (checkedDate) {
      const formatDate = checkedDate.map(item => new Date(item));

      setSelectedDates(formatDate);
    }
  }, [checkedDate]);

  return (
    <CustomCalendarBase
      isMobile={isMobile}
      events={events}
      selectedDates={selectedDates}
      handleDateClick={handleDateClick}
      eventContent={eventInfo => {
        const title = eventInfo.event.title.split('/');

        return (
          <ScheduleCardBox
            className="schedule-card-box"
            onClick={e => {
              e.stopPropagation();

              setCheckedDate([]);
              handleIsOpenUpdateOptionSheet({
                isOpen: true,
                sheetType: OptionSheetType.UPDATE,
                scheduleItem: calendarList?.find(
                  item => item.id === eventInfo.event.extendedProps.id,
                ) as T,
              });
            }}
          >
            <span>{title[0]}~</span>
            <span>{title[1]}</span>
          </ScheduleCardBox>
        );
      }}
    />
  );
};

export default CustomCalendarV2;
